import { memo } from "react";
import { NavLink, To } from "react-router-dom";
import { Checkbox } from "@mui/material";
import { CheckBox } from "../../CheckBoxComponent/checkbox-component";

/**
 * Props for Terms And Conditions Component.
 * @interface
 * @name TermsAndConditionsComponentProps
 * @kind interface
 */
interface TermsAndConditionsComponentProps {
  /**
   * Describes a location that is the destination of some navigation, either via
   * `history.push` or `history.replace`. May be either a URL or the pieces of a
   * URL path.
   * @property
   * @name to
   * @kind property
   * @memberof TermsAndConditionsComponentProps
   * @type {To}
   */
  to: To;
  /**
   * Text that is used in the 'By clicking `{text}`,...'.
   * @property
   * @name text
   * @kind property
   * @memberof TermsAndConditionsComponentProps
   * @type {string}
   */
  text: string;
  /**
   * Callback to handle checkbox change.
   * @property
   * @name onCheckboxChange
   * @kind property
   * @memberof TermsAndConditionsComponentProps
   * @type {() => void}
   */
  onCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Indicates whether the checkbox is checked.
   * @property
   * @name isChecked
   * @kind property
   * @memberof TermsAndConditionsComponentProps
   * @type {boolean}
   */
  isChecked?: boolean;
}

/**
 * Memoized Terms And Conditions Component.
 * @constant
 * @name TermsAndConditionsComponent
 * @kind variable
 * @type {React.NamedExoticComponent<TermsAndConditionsComponentProps>}
 */
const TermsAndConditionsComponent: React.NamedExoticComponent<TermsAndConditionsComponentProps> =
  memo(
    /**
     * Terms And Conditions Component.
     * @function
     * @name TermsAndConditionsComponent
     * @kind function
     * @memberof TermsAndConditionsComponent
     * @param {TermsAndConditionsComponentProps} props Props for Terms And Conditions Component.
     * @returns {JSX.Element} Terms And Conditions Component.
     */
    function TermsAndConditionsComponent(
      props: TermsAndConditionsComponentProps
    ): JSX.Element {
      const { text, to, onCheckboxChange, isChecked } = props;

      const checkboxId = "terms-checkbox";

      return (
        <div className="flex items-center gap-3">
          <CheckBox
            label=""
            state={isChecked ? "checked" : "unchecked"}
            onChange={(nextState) => {
              const checked = nextState === "checked";
              if (onCheckboxChange) {
                // Trigger the original onChange with a synthetic event
                onCheckboxChange({
                  target: { checked },
                } as React.ChangeEvent<HTMLInputElement>);
              }
            }}
          />

          <label
            htmlFor={checkboxId}
            className="cursor-pointer text-sm font-normal leading-snug text-neutral-500"
            style={{ userSelect: "none" }}
          >
            By clicking the {text}, you agree to Finopay's{" "}
            <NavLink
              to={to}
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-600"
              onClick={(e) => e.stopPropagation()}
            >
              Terms and Conditions
            </NavLink>
          </label>
        </div>
      );
    }
  );

export default TermsAndConditionsComponent;
